// <kg-reveal>: blendet einen Bereich ein, sobald er ins Bild kommt, und startet
// die Effekte darin (data-bump, data-shine). Laeuft genau einmal je Bereich.
//
// Markierte Teile (.reveal-part) erscheinen einzeln, sobald sie selbst ins Bild
// kommen. Bei Bild und Text laeuft so nicht alles gleichzeitig: Das Bild erscheint,
// waehrend der Text noch unter der Kante liegt, und kommt dran, sobald er auftaucht.
// Ohne markierte Teile erscheint der Inhalt als Ganzes.
//
// Bei "weniger Bewegung" im Betriebssystem erscheint alles ohne Animation.

const REDUCED = '(prefers-reduced-motion: reduce)';

/*
  Der Effekt startet ein Stueck vor der unteren Fensterkante. Steht dort die Kaufleiste,
  kommt ihre Hoehe dazu, sonst liefe der Effekt hinter der Leiste ab. Prozent und Pixel
  lassen sich in rootMargin nicht mischen, deshalb wird alles in Pixel gerechnet.
*/
const barHeight = () =>
  parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-bar-height')) || 0;

const rootMargin = () => `0px 0px -${Math.round(window.innerHeight * 0.12 + barHeight())}px 0px`;

class KgReveal extends HTMLElement {
  connectedCallback() {
    if (this.dataset.visible !== undefined) return;

    const parts = [...this.querySelectorAll('.reveal-part')];
    this.targets = parts.length ? parts : [this.firstElementChild].filter(Boolean);

    /*
      Was beim Laden schon im Bild steht, erscheint sofort und ohne Bewegung. Grund: Dieses
      Modul laeuft erst nach dem Parsen der Seite. Eine Animation faenge dort merklich zu
      spaet an und liest sich wie Nachladen — besonders beim ersten Element ganz oben.
      Unterhalb der Kante faellt dieselbe Verzoegerung nicht auf, dort bleibt der Effekt.
    */
    for (const target of this.targets) {
      const kasten = target.getBoundingClientRect();
      if (kasten.top >= window.innerHeight || kasten.bottom <= 0) continue;
      target.dataset.instant = '';
      target.dataset.visible = '';
      this.show();
    }

    if (!('IntersectionObserver' in window) || window.matchMedia(REDUCED).matches) {
      for (const target of this.targets) target.dataset.visible = '';
      this.show();
      return;
    }

    this.beobachte();

    /* Die Leiste meldet sich, wenn sie kommt oder geht: der Ausloeser rueckt dann mit */
    this.onBar = () => this.beobachte();
    document.addEventListener('kg:sticky-bar', this.onBar);
    window.addEventListener('resize', this.onBar);
  }

  /* Setzt den Beobachter mit dem aktuellen Rand neu auf, fuer alle noch nicht gezeigten Teile */
  beobachte() {
    const offen = this.targets.filter((target) => target.dataset.visible === undefined);
    this.observer?.disconnect();
    if (!offen.length) {
      if (!this.targets.length) this.show();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.dataset.visible = '';
          this.observer.unobserve(entry.target);
          this.show();
        }
      },
      // Loest aus, sobald ein Stueck des Teils sichtbar wird
      { threshold: 0, rootMargin: rootMargin() },
    );

    for (const target of offen) this.observer.observe(target);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.onBar) {
      document.removeEventListener('kg:sticky-bar', this.onBar);
      window.removeEventListener('resize', this.onBar);
    }
  }

  /* Der Bereich gilt als sichtbar, sobald sein erstes Teil erscheint: daran haengen Bump und Lichtstreifen */
  show() {
    this.dataset.visible = '';
  }
}

if (!customElements.get('kg-reveal')) customElements.define('kg-reveal', KgReveal);
